import type { PlanningAssistantOperation } from "../../_models/planning-assistant.interface";

// Reine Hilfsfunktionen für die 2-Wochen-Ansicht der Kapazitätsübersicht
// (ohne Service-Abhängigkeiten, damit sie direkt testbar sind)

// Tageskapazität: 8h Arbeit + 0:30h Pause = 8,5h = 100 % Auslastung —
// gemeinsame Basis der Kapazitätsprozente in Tages-Balken (Badge), 2-Wochen-
// Grid und Planungs-Seite, damit alle Ansichten identische Werte liefern
export const GRID_SOLL_MINUTES = 8.5 * 60

// Unbezahlte Pause je Arbeitstag: 30 Minuten pro 8h — sie wird wie bei den
// Tages-Balken zur Tourzeit addiert, sobald an dem Tag eine Tour existiert
export const PAUSE_MINUTES_PER_DAY = 30

export interface GridTechnicianInput {
  id: number,
  name: string,
  absence_user_id?: string,
  country?: string,
  state?: string,
}

// Daten der 2-Wochen-Ansicht: je Techniker ein Wert (Prozent) je Arbeitstag;
// null = Abwesenheit/keine Daten. Parallel zu values steht in absences der
// Abwesenheitsgrund des Tages (null = anwesend), damit die Ansicht
// Abwesenheiten sichtbar machen kann
export interface TwoWeekGridData {
  start_date: Date,
  technicians: {
    name: string,
    values: (number | null)[],
    titles: string[],
    absences?: (string | null)[],
  }[]
}

export interface TwoWeekGridParams {
  // Montag der ersten Woche (lokales Datum)
  start_date: Date,
  // Anzahl Wochen (Arbeitstage Mo-Fr), default: 2
  weeks?: number,
  technicians: GridTechnicianInput[],
  operations: PlanningAssistantOperation[],
  // liefert den Abwesenheitsgrund je Techniker+Tag oder null (Feiertag/Urlaub/keine Schicht)
  absence_reason_for: (technician: GridTechnicianInput, date: Date) => string | null,
  // Referenzzeit für Live-Aufträge (finished-Prüfung), default: new Date()
  now?: Date,
}

// Zelle der Grid-Tabelle im Template
export interface GridCellView {
  value: number | null,
  label: string,
  title: string,
  bg: string,
  fg: string,
  // Abwesenheitsgrund des Tages (z.B. 'Urlaub') oder undefined = anwesend
  absence?: string,
}

export interface GridKwRow {
  kind: 'kw',
  label: string,
}

export interface GridDayRow {
  kind: 'day',
  label: string,
  is_today: boolean,
  cells: GridCellView[],
  avg: GridCellView,
}

export type GridRowView = GridKwRow | GridDayRow

export interface GridViewModel {
  rows: GridRowView[],
  techs: string[],
  footer: GridCellView[],
  team: GridCellView,
}

const WEEKDAYS_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']

// Arbeitstage (Mo-Fr) über `weeks` Wochen ab start_date
export function gridWorkdays(start_date: Date, weeks: number = 2): Date[] {
  const days: Date[] = []
  for (let week = 0; week < weeks; week++) {
    for (let day_idx = 0; day_idx < 5; day_idx++) {
      const day = new Date(start_date.getFullYear(), start_date.getMonth(), start_date.getDate())
      day.setDate(day.getDate() + week * 7 + day_idx)
      days.push(day)
    }
  }
  return days
}

// Montag der Woche des Datums (lokal)
export function startOfWeek(date: Date): Date {
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  monday.setDate(monday.getDate() - (monday.getDay() + 6) % 7)
  return monday
}

// ISO-Kalenderwoche
export function isoWeek(date: Date): number {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const day = target.getUTCDay() || 7
  target.setUTCDate(target.getUTCDate() + 4 - day)
  const year_start = new Date(Date.UTC(target.getUTCFullYear(), 0, 1))
  return Math.ceil(((target.getTime() - year_start.getTime()) / 86400000 + 1) / 7)
}

// 'dd.mm.' (Jahr zweistellig angehängt: 'dd.mm.yyyy')
export function formatShortDate(date: Date, with_year?: boolean): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const base = `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.`
  return with_year ? base + date.getFullYear() : base
}

// Farbskala der Grid-Zellen nach Schwellenwerten (identisch zum Auslastungs-Badge)
export function gridCellColor(pct: number | null): { bg: string, fg: string } {
  if (pct == null) return { bg: '#e5e7eb', fg: '#6b7280' }
  if (pct >= 100) return { bg: '#2e9e3f', fg: '#fff' }
  if (pct >= 80) return { bg: '#a8e0a0', fg: '#14532d' }
  if (pct >= 50) return { bg: '#f7cf4d', fg: '#5c4300' }
  return { bg: '#e04b3d', fg: '#fff' }
}

// Kurzkürzel eines Abwesenheitsgrunds für schmale Grid-Zellen und Badges
// (vollständiger Grund steht im Tooltip/Title)
export function absenceCellCode(reason: string): string {
  const normalized = reason.toLowerCase()
  if (normalized.includes('urlaub')) return 'U'
  if (normalized.includes('krank')) return 'K'
  if (normalized.includes('feiertag')) return 'F'
  if (normalized.includes('keine schicht')) return 'S'
  return 'A'
}

// Mittelwert über alle nicht-null Werte (gerundet), null wenn leer
export function averagePercent(values: (number | null)[]): number | null {
  const valid = values.filter(v => v != null)
  if (!valid.length) return null
  return Math.round(valid.reduce((sum, v) => sum + v, 0) / valid.length)
}

// Live gemessene Auftragszeit einer Operation in Minuten (finished − on_site)
// oder null, solange (noch) nicht gemessen wurde — dann gilt die (pausenfreie)
// Schätzung des Tickets
export function operationMeasuredMinutes(operation: PlanningAssistantOperation, now: Date): number | null {
  const finished = operation.date_finished ? new Date(operation.date_finished) : null
  const on_site = operation.date_on_site ? new Date(operation.date_on_site) : null
  if (finished && on_site && finished <= now) {
    return Math.max(0, (finished.getTime() - on_site.getTime()) / (1000 * 60))
  }
  return null
}

// Fahrzeit-Anteil einer Operation aus den Zeitstempeln des Tourenplaners
// (Abfahrt → Ankunft beim Kunden) — dieselbe Basis wie die Fahrtsegmente der
// Tages-Balken. 0, wenn keine Zeitstempel vorliegen
export function operationDriveMinutes(operation: PlanningAssistantOperation): number {
  const travel_start = operation.date_travel_start ? new Date(operation.date_travel_start) : null
  const on_site = operation.date_on_site ? new Date(operation.date_on_site) : null
  if (travel_start && on_site && on_site > travel_start) {
    return (on_site.getTime() - travel_start.getTime()) / (1000 * 60)
  }
  return 0
}

// Tourzeit eines Tages in Minuten: Fahrzeit + Auftragszeit + 30 min Pause —
// identische Zusammensetzung wie die Auslastungs-Badges der Tages-Balken
// (total_hours / 8,5h). Ein Tag ohne Tour bekommt weder Pause noch Prozent
export function dayTourMinutes(drive_minutes: number, work_minutes: number): number {
  const tour_minutes = drive_minutes + work_minutes
  return tour_minutes > 0 ? tour_minutes + PAUSE_MINUTES_PER_DAY : 0
}

function gridCell(value: number | null, title: string, absence?: string): GridCellView {
  return {
    value,
    label: absence ? absenceCellCode(absence) : value == null ? '—' : `${value}%`,
    title,
    ...gridCellColor(value),
    absence,
  }
}

function averageCell(values: (number | null)[], title: string): GridCellView {
  return gridCell(averagePercent(values), title)
}

// Baut das Grid-ViewModel für das Template: KW-Trennzeilen, Tageszeilen mit
// Zellen je Techniker, Team-Mittelwert je Tag sowie Ø-Spalte je Techniker
export function buildGridRows(data: TwoWeekGridData, today: Date): GridViewModel {
  const days = gridWorkdays(data.start_date)
  const rows: GridRowView[] = []

  days.forEach((day, day_idx) => {
    if (day_idx % 5 == 0) {
      const friday = new Date(day)
      friday.setDate(friday.getDate() + 4)
      rows.push({
        kind: 'kw',
        label: `KW ${isoWeek(day)} · ${formatShortDate(day)} – ${formatShortDate(friday)}`,
      })
    }

    const values = data.technicians.map(tech => tech.values[day_idx] ?? null)
    const cells = data.technicians.map((tech, tech_idx) =>
      gridCell(
        values[tech_idx],
        tech.titles[day_idx] || tech.name,
        tech.absences?.[day_idx] ?? undefined,
      )
    )
    rows.push({
      kind: 'day',
      label: `${WEEKDAYS_SHORT[day.getDay()]} ${formatShortDate(day)}`,
      is_today: day.toDateString() == today.toDateString(),
      cells,
      avg: averageCell(values, `Team am ${formatShortDate(day)}`),
    })
  })

  const footer = data.technicians.map(tech =>
    averageCell(tech.values, `Ø 2 Wochen · ${tech.name}`)
  )
  const team = averageCell(footer.map(cell => cell.value), 'Ø 2 Wochen · Team')

  return { rows, techs: data.technicians.map(tech => tech.name), footer, team }
}

// Baut die 2-Wochen-Daten aus Operationen und Abwesenheits-Callback
export function buildTwoWeekGridData(params: TwoWeekGridParams): TwoWeekGridData {
  const days = gridWorkdays(params.start_date, params.weeks ?? 2)
  const now = params.now ?? new Date()

  // Operationen einmalig je Kalendertag gruppieren (schneller Zugriff unten),
  // statt je Techniker und Tag über alle Operationen zu iterieren
  const operations_by_day = new Map<string, PlanningAssistantOperation[]>()
  for (const op of params.operations) {
    const op_date = new Date(op.operation_date)
    const key = `${op_date.getFullYear()}-${op_date.getMonth()}-${op_date.getDate()}`
    const day_operations = operations_by_day.get(key)
    if (day_operations) {
      day_operations.push(op)
    } else {
      operations_by_day.set(key, [op])
    }
  }

  const technicians = params.technicians.map(tech => {
    const values: (number | null)[] = []
    const titles: string[] = []
    const absences: (string | null)[] = []
    for (const day of days) {
      const absence_reason = params.absence_reason_for(tech, day)
      if (absence_reason) {
        values.push(null)
        titles.push(absence_reason)
        absences.push(absence_reason)
        continue
      }
      absences.push(null)

      const day_operations = (operations_by_day.get(`${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`) ?? [])
        .filter(op => op.user_ids.includes(tech.id))
      let drive_minutes = 0
      let work_minutes = 0
      for (const op of day_operations) {
        drive_minutes += operationDriveMinutes(op)
        const measured = operationMeasuredMinutes(op, now)
        if (measured == null) work_minutes += op.ticket?.time_estimation ?? 0
        else work_minutes += measured
      }
      const tour_minutes = dayTourMinutes(drive_minutes, work_minutes)
      values.push(Math.round(tour_minutes / GRID_SOLL_MINUTES * 100))

      const work_hours = (Math.round(work_minutes / 6) / 10).toString().replace('.', ',')
      titles.push(day_operations.length
        ? `${day_operations.length} ${day_operations.length == 1 ? 'Auftrag' : 'Aufträge'} · ${work_hours} h Auftragszeit`
        : 'Keine Aufträge')
    }
    return { name: tech.name, values, titles, absences }
  })

  return { start_date: params.start_date, technicians }
}

// ─── Kapazitätsplanung (Quartals-Grid je Techniker und Kalenderwoche) ───

// Zeiträume der Kapazitätsplanung mit Wochenanzahl
export type PlanningRange = 'Woche' | 'Monat' | 'Quartal' | 'Halbjahr'

export const PLANNING_RANGE_WEEKS: Record<PlanningRange, number> = {
  Woche: 1,
  Monat: 5,
  Quartal: 14,
  Halbjahr: 26,
}

const MONTHS_LONG = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
]

// Monat-Kürzel für schmale Segmente, in die der lange Name nicht passt
const MONTHS_SHORT: Record<string, string> = {
  Januar: 'Jan.', Februar: 'Feb.', März: 'Mär.', April: 'Apr.',
  Mai: 'Mai', Juni: 'Jun.', Juli: 'Jul.', August: 'Aug.',
  September: 'Sep.', Oktober: 'Okt.', November: 'Nov.', Dezember: 'Dez.',
}

// Farbskala der Kapazitätsplanung (Freie-Kapazität-Semantik):
// gelb = viel frei planbar, rot = überbucht
export function capacityPlanningColor(pct: number | null): { bg: string, fg: string } {
  if (pct == null) return { bg: '#e5e7eb', fg: '#6b7280' }
  if (pct >= 105) return { bg: '#e04b3d', fg: '#fff' }
  if (pct >= 95) return { bg: '#2e9e3f', fg: '#fff' }
  if (pct >= 85) return { bg: '#a8e0a0', fg: '#14532d' }
  if (pct >= 75) return { bg: '#e2e88a', fg: '#4d4a0a' }
  return { bg: '#f7cf4d', fg: '#5c4300' }
}

// 'Nachname, Vorname' -> 'Nachname, V.'
export function shortTechnicianName(name: string): string {
  const [last, first] = name.split(', ')
  if (!first) return name
  return `${last}, ${first[0]}.`
}

export interface PlanningCell {
  pct: number | null,
  label: string,
  title: string,
  bg: string,
  fg: string,
  // Abwesenheitsgrund des Tages (z.B. 'Urlaub') — Zelle zeigt das Kürzel
  // (U/K/F/S/A, selbe Mapping wie die Kapazitätsbalken) statt '—'
  absence?: string,
}

// Spaltenkopf: geschlossene KW oder geöffnete Woche (5 Tages-Spalten)
export interface PlanningHead {
  label: string,
  sub: string,
  // Index der zugehörigen Woche (für Auf/Zuklappen)
  week_index: number,
  open: boolean,
  title: string,
}

export interface PlanningMonth {
  label: string,
  // Kürzel (z.B. 'Dez.'), wenn der lange Name nicht in die Segmentbreite passt
  short: string,
  span: number,
}

export interface PlanningRow {
  name: string,
  short: string,
  cells: PlanningCell[],
  avg: PlanningCell,
}

export interface PlanningTable {
  months: PlanningMonth[],
  heads: PlanningHead[],
  team_cells: PlanningCell[],
  team_avg: PlanningCell,
  rows: PlanningRow[],
  cols: number,
  // 'KW n · dd.mm. – dd.mm.' (nur Zeitraum 'Woche', sonst leer)
  week_title: string,
}

export interface PlanningTableOptions {
  range: PlanningRange,
  // aktuell aufgeklappte Woche (null = keine), nur außerhalb von 'Woche'
  open_week?: number | null,
  // Namensfilter (Teilstring, Groß-/Kleinschreibung egal)
  filter?: string,
  // Sortierung: Name (Eingabereihenfolge) oder freie Kapazität zuerst (niedrigster Ø zuerst)
  sort?: 'name' | 'load',
}

function planningCell(pct: number | null, title: string, absence?: string): PlanningCell {
  return {
    pct,
    // Abwesend: Kürzel statt '—'/'%' (selbe Mapping wie die Kapazitätsbalken)
    label: absence ? absenceCellCode(absence) : pct == null ? '—' : `${pct}%`,
    title,
    ...capacityPlanningColor(pct),
    absence,
  }
}

// Baut das Kapazitätsplanungs-Grid: KW-Spalten (auf-/zuklappbar zu Tagen),
// Team-Ø-Zeile, Techniker-Zeilen mit Ø-Spalte, Monatstitel, Filter und Sortierung
export function buildPlanningTable(data: TwoWeekGridData, options: PlanningTableOptions): PlanningTable {
  const is_week_range = options.range == 'Woche'
  const n_weeks = PLANNING_RANGE_WEEKS[options.range]

  // Wochenanfänge (Montage) des Zeitraums
  const weeks = gridWorkdays(data.start_date, n_weeks)
    .filter(day => day.getDay() == 1)
    .map((mon, w) => ({
      week_index: w,
      mon,
      kw: isoWeek(mon),
      month: MONTHS_LONG[mon.getMonth()],
      open: is_week_range || options.open_week === w,
    }))

  // Monatstitel: aufeinanderfolgende Wochen desselben Monats zusammenfassen
  const months: PlanningMonth[] = []
  for (const week of weeks) {
    const span = week.open ? 5 : 1
    const last = months[months.length - 1]
    if (last && last.label === week.month) {
      last.span += span
    } else {
      months.push({ label: week.month, short: MONTHS_SHORT[week.month] ?? week.month, span })
    }
  }

  // Spaltenköpfe
  const heads: PlanningHead[] = []
  for (const week of weeks) {
    if (week.open) {
      for (let i = 0; i < 5; i++) {
        const day = new Date(week.mon)
        day.setDate(day.getDate() + i)
        heads.push({
          label: WEEKDAYS_SHORT[day.getDay()],
          sub: formatShortDate(day),
          week_index: week.week_index,
          open: true,
          title: `KW ${week.kw} zuklappen`,
        })
      }
    } else {
      heads.push({
        label: `KW ${week.kw}`,
        sub: formatShortDate(week.mon),
        week_index: week.week_index,
        open: false,
        title: `Tage der KW ${week.kw} anzeigen`,
      })
    }
  }

  // Zeilen (Filter gegen den vollen Namen 'Nachname, Vorname')
  const filter_text = (options.filter ?? '').trim().toLowerCase()
  let rows: PlanningRow[] = data.technicians
    .map(tech => {
      const short = shortTechnicianName(tech.name)
      const values = tech.values
      const absences = tech.absences ?? []
      const cells: PlanningCell[] = []
      for (const week of weeks) {
        const day_offset = week.week_index * 5
        const week_values = [0, 1, 2, 3, 4].map(i => values[day_offset + i] ?? null)
        const week_absences = [0, 1, 2, 3, 4].map(i => absences[day_offset + i] ?? null)
        if (week.open) {
          for (let i = 0; i < 5; i++) {
            const day = new Date(week.mon)
            day.setDate(day.getDate() + i)
            const absence = week_absences[i]
            cells.push(planningCell(
              week_values[i],
              absence
                ? `${absence} · ${WEEKDAYS_SHORT[day.getDay()]} ${formatShortDate(day)} · ${tech.name}`
                : `${WEEKDAYS_SHORT[day.getDay()]} ${formatShortDate(day)} · ${tech.name}`,
              absence ?? undefined,
            ))
          }
        } else {
          // Geschlossene KW: Nur wenn die ganze Woche gleichmäßig abwesend
          // ist (z.B. Urlaub), wird das Kürzel statt des (leeren) Mittels
          // gezeigt — ansonsten zählt der Durchschnitt der Arbeitstage
          const full_week_absence = week_absences.every(a => a != null) ? week_absences[0] : null
          cells.push(planningCell(
            full_week_absence ? null : averagePercent(week_values),
            full_week_absence
              ? `${full_week_absence} · KW ${week.kw} · ${tech.name}`
              : `KW ${week.kw} · ${tech.name}`,
            full_week_absence ?? undefined,
          ))
        }
      }
      const avg = planningCell(averagePercent(values), `Ø ${options.range} · ${tech.name}`)
      return { name: tech.name, short, cells, avg }
    })
    .filter(row => !filter_text || row.name.toLowerCase().includes(filter_text))

  // Sortierung: freie Kapazität zuerst = niedrigster Ø zuerst (ohne Ø nach hinten)
  if (options.sort == 'load') {
    rows = [...rows].sort((a, b) => (a.avg.pct ?? 999) - (b.avg.pct ?? 999))
  }

  // Team-Zeile: Mittelwert über die angezeigten (gefilterten) Zeilen
  const team_cells = heads.map((_head, i) =>
    planningCell(averagePercent(rows.map(row => row.cells[i].pct)), 'Team Ø')
  )
  const team_avg = planningCell(
    averagePercent(rows.map(row => row.avg.pct)),
    'Team Ø gesamt'
  )

  const first_monday = weeks[0]?.mon ?? data.start_date
  const first_friday = new Date(first_monday)
  first_friday.setDate(first_friday.getDate() + 4)

  return {
    months,
    heads,
    team_cells,
    team_avg,
    rows,
    cols: heads.length + 2,
    week_title: is_week_range
      ? `KW ${isoWeek(first_monday)} · ${formatShortDate(first_monday)} – ${formatShortDate(first_friday)}`
      : '',
  }
}
